import React, { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Search, Edit3, Trash2, ArrowRightLeft, Plus, Package, Calendar, User as UserIcon, RefreshCw, ImageOff, X } from "lucide-react";

/**
 * 📦 Echipament Alocat (admin + magazioner)
 *
 * Tabel plat cu toate preluările/alocările (din contul angajatului SAU manuale).
 * Acțiuni per rând: ✏️ Editează, ↔ Mută, 🗑 Șterge. Sus: ➕ Alocă manual.
 */
export default function EchipamentAlocat() {
  const { user } = useAuth();
  const canManage = ["admin", "magazioner"].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterUser, setFilterUser] = useState("__all__");
  const [filterType, setFilterType] = useState("__all__");
  const [editing, setEditing] = useState(null);
  const [transferring, setTransferring] = useState(null);
  const [allocateOpen, setAllocateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { photoId, name }

  // Selectoare pentru transfer & alocare manuală
  const [people, setPeople] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [projects, setProjects] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const params = ["limit=1000"];
      if (filterUser !== "__all__") params.push(`user_id=${encodeURIComponent(filterUser)}`);
      const { data } = await api.get(`/inventory/issuances?${params.join("&")}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error(e);
      toast.error("Eroare la încărcarea preluărilor");
      setRows([]);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!canManage) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, filterUser]);

  useEffect(() => {
    if (!canManage) return;
    Promise.all([
      api.get("/persons?status=active").then((r) => setPeople(r.data || [])).catch(() => {}),
      api.get("/inventory").then((r) => setInventory((r.data || []).filter((x) => (x.quantity || 0) > 0 && x.status !== "defect"))).catch(() => {}),
      api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {}),
    ]);
  }, [canManage]);

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    } catch { return iso; }
  };

  const uniqueUsers = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => { if (r.user_id && !map.has(r.user_id)) map.set(r.user_id, r.user_name || r.user_id); });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (filterType !== "__all__") {
        if (filterType === "manual" && !r.allocated_by_admin) return false;
        if (filterType === "self" && r.allocated_by_admin) return false;
        if (filterType === "transferred" && !r.transferred_at) return false;
      }
      if (!q) return true;
      const hay = `${r.item_name || ""} ${r.item_serial || ""} ${r.user_name || ""} ${r.note || ""} ${r.project_name || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, filterType]);

  // Sumar (totaluri pe lista filtrată)
  const summary = useMemo(() => {
    const itemMap = new Map();
    let totalQty = 0;
    for (const r of filtered) {
      const qty = parseFloat(r.quantity) || 0;
      totalQty += qty;
      const key = r.item_name || "—";
      const e = itemMap.get(key) || { name: key, qty: 0, unit: r.item_unit || "buc" };
      e.qty += qty;
      itemMap.set(key, e);
    }
    const topItems = Array.from(itemMap.values()).sort((a, b) => b.qty - a.qty).slice(0, 5);
    return { totalQty, topItems, count: filtered.length };
  }, [filtered]);

  // ---- Actions ---------------------------------------------------------
  const openEdit = (r) => setEditing({ id: r.id, quantity: String(r.quantity || ""), note: r.note || "", project_id: r.project_id || "", item_name: r.item_name, item_unit: r.item_unit || "buc", user_name: r.user_name, ts: r.ts });

  const saveEdit = async () => {
    if (!editing) return;
    const qty = parseFloat(editing.quantity);
    if (!qty || qty <= 0) { toast.error("Cantitatea trebuie să fie pozitivă"); return; }
    setBusy(true);
    try {
      await api.put(`/inventory/issuances/${editing.id}`, { quantity: qty, note: editing.note.trim() || null, project_id: editing.project_id || null });
      toast.success("Preluare actualizată — stocul ajustat");
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la salvare"); } finally { setBusy(false); }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Anulezi preluarea de ${r.quantity} ${r.item_unit || "buc"} din "${r.item_name}" făcută de ${r.user_name}?\n\nCantitatea va fi returnată în stoc.`)) return;
    try {
      await api.delete(`/inventory/issuances/${r.id}`);
      toast.success("Preluare anulată — stoc restaurat");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la ștergere"); }
  };

  const openTransfer = (r) => setTransferring({ id: r.id, user_id: "", item_name: r.item_name, qty: r.quantity, current_user_name: r.user_name });
  const saveTransfer = async () => {
    if (!transferring?.user_id) { toast.error("Alege noul angajat"); return; }
    setBusy(true);
    try {
      await api.post(`/inventory/issuances/${transferring.id}/transfer`, { user_id: transferring.user_id });
      toast.success("Preluare mutată către alt angajat");
      setTransferring(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la transfer"); } finally { setBusy(false); }
  };

  if (!canManage) {
    return <div className="p-6 text-center text-sm text-zinc-500">Acces restricționat. Doar admin sau magazioner pot vedea acest modul.</div>;
  }

  return (
    <div className="p-2" data-testid="page-echipament-alocat">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold">📦 Echipament Alocat</h1>
          <p className="text-xs text-zinc-500 mt-1">
            Tot ce și-au alocat angajații din inventar (preluări din cont propriu &bdquo;Ia din Inventar&rdquo; sau alocate manual).
            <strong className="text-zinc-700"> Modifică, mută, șterge sau alocă echipamente direct de aici.</strong>
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ea-refresh-btn">
            <RefreshCw size={14} className={loading ? "animate-spin" : ""}/> Reîncarcă
          </Button>
          <Button size="sm" onClick={() => setAllocateOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ea-open-allocate-btn">
            <Plus size={14}/> Alocă manual
          </Button>
        </div>
      </div>

      {/* Filtre */}
      <div className="card-flat p-3 mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400"/>
          <Input
            placeholder="Caută angajat, articol, serie, notă, proiect…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-9 pl-8 text-sm"
            data-testid="ea-filter-search"
          />
        </div>
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="h-9 w-[220px] text-sm" data-testid="ea-filter-user">
            <SelectValue placeholder="Toți angajații"/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">— Toți angajații —</SelectItem>
            {uniqueUsers.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="ea-filter-type">
            <SelectValue/>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Toate alocările</SelectItem>
            <SelectItem value="self">Auto-preluate</SelectItem>
            <SelectItem value="manual">Alocate manual</SelectItem>
            <SelectItem value="transferred">Transferate</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-xs text-zinc-500">
          <strong className="text-zinc-800 tabular-nums">{summary.count}</strong>{" "}
          {summary.count === 1 ? "preluare" : "preluări"} · total <strong className="text-zinc-800 tabular-nums">{summary.totalQty}</strong> buc/UM
        </div>
      </div>

      {/* Top 5 articole */}
      {summary.topItems.length > 0 && (
        <div className="card-flat p-3 mb-3" data-testid="ea-top-items">
          <div className="text-[10px] uppercase font-bold tracking-wider text-zinc-500 mb-2">📊 Top 5 articole alocate</div>
          <div className="flex flex-wrap gap-2">
            {summary.topItems.map((t) => (
              <div key={t.name} className="px-2 py-1 rounded bg-zinc-100 border border-zinc-300 text-xs">
                <strong>{t.name}</strong>{" "}
                <span className="text-zinc-600 tabular-nums">— {t.qty} {t.unit}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabel preluări */}
      <div className="card-flat overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic">Se încarcă…</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-sm text-zinc-500 italic" data-testid="ea-empty-state">
            Nicio preluare găsită. Când angajații folosesc &bdquo;Ia din Inventar&rdquo;, înregistrările apar aici.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500">
                  <th className="px-3 py-2"><Calendar size={11} className="inline mr-1"/>Data / Ora</th>
                  <th className="px-2 py-2 w-[56px] text-center">Foto</th>
                  <th className="px-3 py-2"><UserIcon size={11} className="inline mr-1"/>Angajat</th>
                  <th className="px-3 py-2"><Package size={11} className="inline mr-1"/>Articol</th>
                  <th className="px-3 py-2 text-right">Cantitate</th>
                  <th className="px-3 py-2">Proiect</th>
                  <th className="px-3 py-2">Notă</th>
                  <th className="px-3 py-2 w-[140px] text-right">Acțiuni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100 hover:bg-zinc-50/50" data-testid={`ea-row-${r.id}`}>
                    <td className="px-3 py-2 font-mono text-xs tabular-nums whitespace-nowrap">{fmtDate(r.ts)}</td>
                    <td className="px-2 py-2 text-center">
                      {r.photo_id ? (
                        <button
                          type="button"
                          onClick={() => setLightbox({ photoId: r.photo_id, name: r.item_name || "Echipament" })}
                          className="block w-10 h-10 rounded-md overflow-hidden border border-zinc-200 hover:border-emerald-500 hover:ring-2 hover:ring-emerald-200 transition-all bg-zinc-100"
                          title="Vezi poza echipamentului"
                          data-testid={`ea-thumb-${r.id}`}
                        >
                          <img
                            src={fileUrl(r.photo_id)}
                            alt={r.item_name || "echipament"}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        </button>
                      ) : (
                        <div
                          className="w-10 h-10 rounded-md border border-zinc-300 flex items-center justify-center font-bold text-[12px] uppercase text-emerald-700 bg-emerald-50 select-none"
                          title={`${r.item_name || "Articol"} — fără poză în catalog`}
                          data-testid={`ea-thumb-fallback-${r.id}`}
                        >
                          {(r.item_name || "?").trim().slice(0, 2)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 font-semibold">{r.user_name || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="font-semibold flex items-center gap-1 flex-wrap">
                        {r.item_name || "—"}
                        {r.item_serial && (
                          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300" title="Număr de serie">
                            #{r.item_serial}
                          </span>
                        )}
                        {r.allocated_by_admin && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">Manual</span>}
                        {r.transferred_at && <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 font-bold uppercase tracking-wider" title={`Transferată din ${r.transferred_from_user_name || "—"}`}>Mutată</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">{r.quantity} <span className="text-[10px] text-zinc-500 font-normal">{r.item_unit || "buc"}</span></td>
                    <td className="px-3 py-2 text-xs text-zinc-700">{r.project_name || <span className="text-zinc-400">—</span>}</td>
                    <td className="px-3 py-2 text-xs text-zinc-700 max-w-[260px] truncate" title={r.note || ""}>{r.note || <span className="text-zinc-400">—</span>}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(r)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded" title="Editează" data-testid={`ea-edit-${r.id}`}>
                        <Edit3 size={14}/>
                      </button>
                      <button onClick={() => openTransfer(r)} className="text-purple-600 hover:bg-purple-50 p-1.5 rounded ml-1" title="Mută la alt angajat" data-testid={`ea-transfer-${r.id}`}>
                        <ArrowRightLeft size={14}/>
                      </button>
                      <button onClick={() => deleteRow(r)} className="text-red-600 hover:bg-red-50 p-1.5 rounded ml-1" title="Anulează (returnează stocul)" data-testid={`ea-delete-${r.id}`}>
                        <Trash2 size={14}/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* === LIGHTBOX (thumbnail zoom) ====================================== */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="ea-lightbox"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-4 right-4 text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2"
            title="Închide"
            data-testid="ea-lightbox-close"
          >
            <X size={22}/>
          </button>
          <div className="max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
            <img
              src={fileUrl(lightbox.photoId)}
              alt={lightbox.name}
              className="max-w-[95vw] max-h-[80vh] object-contain rounded-lg shadow-2xl"
            />
            <div className="text-white/90 text-sm font-medium text-center px-3 py-1 bg-black/40 rounded">
              {lightbox.name}
            </div>
          </div>
        </div>
      )}

      {/* === EDIT DIALOG ====================================================== */}
      <Dialog open={!!editing} onOpenChange={(v) => { if (!v && !busy) setEditing(null); }}>
        <DialogContent className="max-w-md" data-testid="ea-edit-dialog">
          <DialogHeader><DialogTitle>✏️ Editează preluarea</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3 text-sm">
              <div className="card-flat p-3 bg-zinc-50 space-y-1">
                <div className="flex justify-between text-xs"><span className="text-zinc-500">Articol</span><strong>{editing.item_name}</strong></div>
                <div className="flex justify-between text-xs"><span className="text-zinc-500">Preluat de</span><strong>{editing.user_name}</strong></div>
                <div className="flex justify-between text-xs"><span className="text-zinc-500">La data</span><strong className="font-mono">{fmtDate(editing.ts)}</strong></div>
              </div>
              <div>
                <Label>Cantitate ({editing.item_unit})</Label>
                <Input type="number" min={0.01} step={0.01} value={editing.quantity} onChange={(e) => setEditing({ ...editing, quantity: e.target.value })} data-testid="ea-edit-qty"/>
                <div className="text-[10px] text-zinc-500 mt-1">⚠ Schimbarea cantității ajustează automat stocul.</div>
              </div>
              <div>
                <Label>Proiect (opțional)</Label>
                <Select value={editing.project_id || "__none__"} onValueChange={(v) => setEditing({ ...editing, project_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="—"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Niciunul —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title || p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notă</Label>
                <Textarea rows={2} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} data-testid="ea-edit-note"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={busy}>Anulează</Button>
            <Button onClick={saveEdit} disabled={busy} data-testid="ea-save-edit">{busy ? "Se salvează…" : "Salvează"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === TRANSFER DIALOG ================================================== */}
      <Dialog open={!!transferring} onOpenChange={(v) => { if (!v && !busy) setTransferring(null); }}>
        <DialogContent className="max-w-md" data-testid="ea-transfer-dialog">
          <DialogHeader><DialogTitle>↔ Mută preluarea către alt angajat</DialogTitle></DialogHeader>
          {transferring && (
            <div className="space-y-3 text-sm">
              <div className="card-flat p-3 bg-purple-50 space-y-1 border border-purple-200">
                <div className="text-xs"><strong>{transferring.qty}</strong> × {transferring.item_name}</div>
                <div className="text-xs text-zinc-600">Acum la: <strong>{transferring.current_user_name}</strong></div>
                <div className="text-[10px] text-zinc-500 mt-1">💡 Stocul nu se modifică — doar proprietarul preluării se schimbă.</div>
              </div>
              <div>
                <Label>Mută la</Label>
                <Select value={transferring.user_id || ""} onValueChange={(v) => setTransferring({ ...transferring, user_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="ea-transfer-target"><SelectValue placeholder="Alege angajatul…"/></SelectTrigger>
                  <SelectContent>
                    {people.map((p) => {
                      const pname = p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim() || p.id;
                      return <SelectItem key={p.id} value={p.id}>{pname}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferring(null)} disabled={busy}>Anulează</Button>
            <Button onClick={saveTransfer} disabled={busy || !transferring?.user_id} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="ea-save-transfer">{busy ? "Se mută…" : "Confirmă transferul"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === MANUAL ALLOCATE DIALOG =========================================== */}
      <AllocateDialog
        open={allocateOpen}
        onOpenChange={setAllocateOpen}
        people={people}
        inventory={inventory}
        projects={projects}
        onCreated={() => { setAllocateOpen(false); load(); }}
      />
    </div>
  );
}

function AllocateDialog({ open, onOpenChange, people, inventory, projects, onCreated }) {
  const [form, setForm] = useState({ user_id: "", item_id: "", quantity: "1", project_id: "", note: "" });
  const [busy, setBusy] = useState(false);
  const [itemSearch, setItemSearch] = useState("");

  useEffect(() => {
    if (open) { setForm({ user_id: "", item_id: "", quantity: "1", project_id: "", note: "" }); setItemSearch(""); }
  }, [open]);

  const selectedItem = useMemo(() => inventory.find((x) => x.id === form.item_id), [inventory, form.item_id]);

  const filteredItems = useMemo(() => {
    const q = itemSearch.trim().toLowerCase();
    let arr = inventory.filter((x) => parseFloat(x.quantity || 0) > 0);
    if (q) {
      arr = arr.filter((x) =>
        (x.name || "").toLowerCase().includes(q) ||
        (x.serial || "").toLowerCase().includes(q) ||
        (x.item_type || "").toLowerCase().includes(q)
      );
    }
    return arr.slice(0, 200);
  }, [inventory, itemSearch]);

  const submit = async () => {
    if (!form.user_id) { toast.error("Alege angajatul"); return; }
    if (!form.item_id) { toast.error("Alege articolul"); return; }
    const qty = parseFloat(form.quantity);
    if (!qty || qty <= 0) { toast.error("Cantitatea trebuie să fie pozitivă"); return; }
    if (selectedItem && qty > (parseFloat(selectedItem.quantity) || 0)) {
      toast.error(`Stoc insuficient (max ${selectedItem.quantity} ${selectedItem.unit || "buc"})`); return;
    }
    setBusy(true);
    try {
      await api.post(`/inventory/issuances/manual`, {
        user_id: form.user_id,
        item_id: form.item_id,
        quantity: qty,
        project_id: form.project_id || null,
        note: form.note.trim() || null,
      });
      toast.success("Articol alocat cu succes");
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la alocare"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onOpenChange(false); }}>
      <DialogContent className="max-w-md" data-testid="ea-allocate-dialog">
        <DialogHeader><DialogTitle>➕ Alocă manual unui angajat</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Angajat</Label>
            <Select value={form.user_id || ""} onValueChange={(v) => setForm({ ...form, user_id: v })}>
              <SelectTrigger className="mt-1" data-testid="ea-allocate-user"><SelectValue placeholder="Alege angajatul…"/></SelectTrigger>
              <SelectContent>
                {people.map((p) => {
                  const pname = p.nume_full || `${p.prenume || ""} ${p.nume || ""}`.trim() || p.id;
                  return <SelectItem key={p.id} value={p.id}>{pname}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Articol din inventar</Label>
            <div className="relative mt-1">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"/>
              <Input
                value={itemSearch}
                onChange={(e) => setItemSearch(e.target.value)}
                placeholder="Caută articol după nume, serie, tip..."
                className="pl-8"
                data-testid="ea-allocate-item-search"
              />
            </div>
            {selectedItem && (
              <div className="mt-2 flex items-center gap-2 p-2 rounded border-2 border-emerald-400 bg-emerald-50">
                {selectedItem.photo_ids?.[0] ? (
                  <img src={fileUrl(selectedItem.photo_ids[0])} alt={selectedItem.name} className="w-12 h-12 object-cover rounded border border-emerald-300 bg-white flex-shrink-0"/>
                ) : (
                  <div className="w-12 h-12 rounded border border-emerald-300 bg-white flex items-center justify-center text-zinc-400 flex-shrink-0"><Package size={20}/></div>
                )}
                <div className="flex-1 min-w-0 text-xs">
                  <div className="font-bold truncate">✓ {selectedItem.name}</div>
                  {selectedItem.serial && <div className="text-[10px] font-mono text-amber-800">SN: {selectedItem.serial}</div>}
                  <div className="text-[10px] text-emerald-700">Stoc: {selectedItem.quantity} {selectedItem.unit || "buc"}</div>
                </div>
                <button type="button" onClick={() => setForm({ ...form, item_id: "" })} className="text-zinc-400 hover:text-red-600 px-1" title="Schimbă articolul" data-testid="ea-allocate-item-clear">
                  <X size={14}/>
                </button>
              </div>
            )}
            {!selectedItem && (
              <div className="mt-2 grid grid-cols-3 gap-2 max-h-[280px] overflow-y-auto p-1 border rounded bg-zinc-50" data-testid="ea-allocate-item-grid">
                {filteredItems.length === 0 ? (
                  <div className="col-span-3 text-center text-xs text-zinc-400 italic py-6">Niciun articol disponibil</div>
                ) : filteredItems.map((x) => {
                  const photoId = (x.photo_ids && x.photo_ids.length) ? x.photo_ids[0] : null;
                  return (
                    <button
                      key={x.id}
                      type="button"
                      onClick={() => setForm({ ...form, item_id: x.id })}
                      className="border rounded bg-white p-1.5 hover:border-emerald-400 hover:shadow-md transition-all text-left"
                      data-testid={`ea-allocate-item-pick-${x.id}`}
                    >
                      {photoId ? (
                        <img src={fileUrl(photoId)} alt={x.name} className="w-full aspect-square object-cover rounded border border-zinc-200 bg-zinc-50" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                      ) : (
                        <div className="w-full aspect-square rounded border border-zinc-200 bg-zinc-100 flex items-center justify-center text-zinc-400"><Package size={28}/></div>
                      )}
                      <div className="text-[11px] font-semibold mt-1 leading-tight line-clamp-2" title={x.name}>{x.name}</div>
                      {x.serial && <div className="text-[9px] font-mono text-amber-800 truncate" title={x.serial}>{x.serial}</div>}
                      <div className="text-[9px] text-emerald-700 font-bold">stoc {x.quantity} {x.unit || "buc"}</div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div>
            <Label>Cantitate{selectedItem ? ` (${selectedItem.unit || "buc"})` : ""}</Label>
            <Input type="number" min={0.01} step={0.01} value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="ea-allocate-qty"/>
          </div>
          <div>
            <Label>Proiect (opțional)</Label>
            <Select value={form.project_id || "__none__"} onValueChange={(v) => setForm({ ...form, project_id: v === "__none__" ? "" : v })}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="—"/></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— Niciunul —</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.title || p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notă</Label>
            <Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="ea-allocate-note"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Anulează</Button>
          <Button onClick={submit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ea-save-allocate">{busy ? "Se alocă…" : "Alocă"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
